import { EventBookType, Event, MultilingualText, UUID } from "@features/events/types";
import { validateUrl } from "./urlValidation";

export interface ValidationErrors {
  [key: string]: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

/**
 * Helper function to validate UUID array fields
 */
function validateUUIDArray(
  field: UUID[] | undefined,
  fieldName: string,
  errors: ValidationErrors,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
  translationKey: string
): void {
  if (!field || field.length === 0) {
    errors[fieldName] = t("common.requiredField", {
      field_name: t(translationKey),
    });
  } else {
    // Validate each UUID in the array
    field.forEach((uuid, index) => {
      if (!uuid || !uuid.trim()) {
        errors[`${fieldName}.${index}`] = t("common.requiredField", {
          field_name: `${t(translationKey)} ${index + 1}`,
        });
      }
    });
  }
}

/**
 * Validates media items that are required when the parent media array exists
 */
export function validateMediaItems(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mediaItems: any[] | undefined,
  fieldPrefix: string,
  errors: ValidationErrors,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string
): void {
  if (mediaItems && mediaItems.length > 0) {
    mediaItems.forEach((mediaItem, index) => {
      if (
        !mediaItem.type ||
        (mediaItem.type !== "img" && mediaItem.type !== "vid")
      ) {
        errors[`${fieldPrefix}.${index}.type`] = t("common.requiredField", {
          field_name: `Media ${index + 1} Type`,
        });
      }
      if (!mediaItem.source) {
        errors[`${fieldPrefix}.${index}.source`] = t("common.requiredField", {
          field_name: `Media ${index + 1} Source`,
        });
      }
    });
  }
}

/**
 * Validates required fields for event creation/update
 */
export function validateEventRequiredFields(
  eventData: Partial<Event>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
  selectedLanguages: string[] = []
): ValidationResult {
  const errors: ValidationErrors = {};

  // UNCONDITIONALLY REQUIRED FIELDS

  // eventId (if this is an update)
  if (eventData.id !== undefined && (!eventData.id || !eventData.id.trim())) {
    errors["id"] = t("common.requiredField", {
      field_name: "Event ID",
    });
  }

  // formatId
  if (!eventData.formatId?.trim()) {
    errors["formatId"] = t("common.requiredField", {
      field_name: t("eventDetails.eventTypeAudience.type"),
    });
  }

  // Slug validation (URL handle)
  if (!eventData.slug?.trim()) {
    errors["slug"] = t("common.requiredField", {
      field_name: t("eventDetails.overview.urlHandle"),
    });
  }

  // Event source URL validation - optional, but HTTPS-only when provided
  if (eventData.sourceUrl?.trim()) {
    const sourceUrlValidation = validateUrl(eventData.sourceUrl, {
      requireHttps: true,
      allowEmpty: false,
    });

    if (!sourceUrlValidation.isValid) {
      errors["sourceUrl"] =
        sourceUrlValidation.errorCode === "invalid_https"
          ? t("common.website_field.invalid_https")
          : t("common.website_field.invalid_url");
    }
  }

  // Title validation - unconditionally required multilingual
  validateMultilingualField(
    eventData.title,
    selectedLanguages,
    "title",
    errors,
    t,
    "eventDetails.overview.title",
    true // required
  );

  // Description validation - unconditionally required multilingual
  validateMultilingualField(
    eventData.description,
    selectedLanguages,
    "description",
    errors,
    t,
    "eventDetails.overview.description",
    true // required
  );

  // Expertise levels validation
  validateUUIDArray(
    eventData.expertiseLevelIds,
    "expertiseLevelIds",
    errors,
    t,
    "eventDetails.eventTypeAudience.expertiseLevel"
  );

  // Categories validation
  validateUUIDArray(
    eventData.categoryIds,
    "categoryIds",
    errors,
    t,
    "eventDetails.eventTypeAudience.categories"
  );

  // Organizers validation
  validateUUIDArray(
    eventData.organizerIds,
    "organizerIds",
    errors,
    t,
    "eventDetails.organizers.title"
  );

  // Instructors validation
  validateUUIDArray(
    eventData.instructorIds,
    "instructorIds",
    errors,
    t,
    "eventDetails.instructors.title"
  );

  // Venues/Locations validation
  validateUUIDArray(
    eventData.venueIds,
    "venueIds",
    errors,
    t,
    "eventDetails.locations.title"
  );

  // Language codes validation - unconditionally required
  if (!eventData.languageCodes || eventData.languageCodes.length === 0) {
    errors["languageCodes"] = t("common.requiredField", {
      field_name: t("eventDetails.eventTypeAudience.languages"),
    });
  }

  // Agenda validation - unconditionally required
  if (!eventData.agenda || eventData.agenda.length === 0) {
    errors["agenda"] = t("common.requiredField", {
      field_name: t("eventDetails.agenda.title"),
    });
  }

  // Timezone validation - unconditionally required
  if (!eventData.timezone) {
    errors["timezone"] = t("common.requiredField", {
      field_name: t("eventDetails.agenda.timezone"),
    });
  }

  // Book field validation
  if (!eventData.book) {
    errors["book"] = t("common.requiredField", {
      field_name: t("eventDetails.payment.bookingType"),
    });
  } else if (eventData.book === EventBookType.REDIRECT) {
    // If book is "redirect", bookRedirectLink is required
    if (!eventData.bookRedirectLink || !eventData.bookRedirectLink.trim()) {
      errors["bookRedirectLink"] = t("common.requiredField", {
        field_name: t("eventDetails.payment.bookRedirectLink"),
      });
    } else {
      // Validate URL format
      try {
        new URL(eventData.bookRedirectLink);
      } catch {
        errors["bookRedirectLink"] = t("common.invalidUrl", {
          field_name: t("eventDetails.payment.bookRedirectLink"),
        });
      }
    }
  } else if (eventData.book === EventBookType.PAY) {
    // If book is "pay", bookRedirectLink should be null
    if (eventData.bookRedirectLink !== null && eventData.bookRedirectLink !== undefined) {
      errors["bookRedirectLink"] = t("common.invalidValue", {
        field_name: t("eventDetails.payment.bookRedirectLink"),
        expected: "null",
      });
    }
  }

  // CONDITIONAL VALIDATIONS - Only required if parent object exists

  // Related Events validation (if present)
  if (eventData.relatedEvents && eventData.relatedEvents.length > 0) {
    eventData.relatedEvents.forEach((relatedEvent, index) => {
      if (!relatedEvent.id || !relatedEvent.id.trim()) {
        errors[`relatedEvents.${index}.id`] = t("common.requiredField", {
          field_name: `Related Event ${index + 1} ID`,
        });
      }
      if (!relatedEvent.relation || !relatedEvent.relation.trim()) {
        errors[`relatedEvents.${index}.relation`] = t("common.requiredField", {
          field_name: `Related Event ${index + 1} Relation`,
        });
      }
    });
  }

  // Agenda validation (if present)
  if (eventData.agenda && eventData.agenda.length > 0) {
    eventData.agenda.forEach((day, dayIndex) => {
      // Date is required for each agenda day
      if (!day.date || !day.date.trim()) {
        errors[`agenda.${dayIndex}.date`] = t("common.requiredField", {
          field_name: `Agenda Day ${dayIndex + 1} Date`,
        });
      }

      // Swimlines are required
      if (!day.swimlines || day.swimlines.length === 0) {
        errors[`agenda.${dayIndex}.swimlines`] = t("common.requiredField", {
          field_name: `Agenda Day ${dayIndex + 1} Swimlines`,
        });
      } else {
        day.swimlines.forEach((swimline, swimlineIndex) => {
          const swimlinePrefix = `agenda.${dayIndex}.swimlines.${swimlineIndex}`;

          // Swimline title is required (multilingual)
          if (!hasMultilingualContent(swimline.title)) {
            errors[`${swimlinePrefix}.title`] = t("common.requiredField", {
              field_name: `Swimline ${swimlineIndex + 1} Title`,
            });
          }

          // Validate multilingual swimline title (required_with)
          validateRequiredWithMultilingual(
            swimline.title,
            selectedLanguages,
            `${swimlinePrefix}.title`,
            errors,
            t,
            `Swimline ${swimlineIndex + 1} Title`
          );

          // Sessions are required
          if (!swimline.sessions || swimline.sessions.length === 0) {
            errors[`${swimlinePrefix}.sessions`] = t("common.requiredField", {
              field_name: `Swimline ${swimlineIndex + 1} Sessions`,
            });
          } else {
            swimline.sessions.forEach((session, sessionIndex) => {
              const sessionPrefix = `${swimlinePrefix}.sessions.${sessionIndex}`;

              // Session required fields
              if (
                session.multiLine === undefined ||
                session.multiLine === null
              ) {
                errors[`${sessionPrefix}.multiLine`] = t(
                  "common.requiredField",
                  {
                    field_name: `Session ${sessionIndex + 1} MultiLine`,
                  }
                );
              }
              if (!session.from || !session.from.trim()) {
                errors[`${sessionPrefix}.from`] = t("common.requiredField", {
                  field_name: `Session ${sessionIndex + 1} From Time`,
                });
              }
              if (!session.to || !session.to.trim()) {
                errors[`${sessionPrefix}.to`] = t("common.requiredField", {
                  field_name: `Session ${sessionIndex + 1} To Time`,
                });
              }
              if (!hasMultilingualContent(session.title)) {
                errors[`${sessionPrefix}.title`] = t("common.requiredField", {
                  field_name: `Session ${sessionIndex + 1} Title`,
                });
              }

              // Validate multilingual session title (required_with)
              validateRequiredWithMultilingual(
                session.title,
                selectedLanguages,
                `${sessionPrefix}.title`,
                errors,
                t,
                `Session ${sessionIndex + 1} Title`
              );

              // Session instructor IDs (if present)
              if (session.instructorIds && session.instructorIds.length > 0) {
                session.instructorIds.forEach(
                  (instructorId, instructorIndex) => {
                    if (!instructorId || !instructorId.trim()) {
                      errors[
                        `${sessionPrefix}.instructorIds.${instructorIndex}`
                      ] = t("common.requiredField", {
                        field_name: `Session ${sessionIndex + 1} Instructor ${
                          instructorIndex + 1
                        }`,
                      });
                    }
                  }
                );
              }

              // Session allowed ticket IDs (if present)
              if (
                session.allowedByTicketIds &&
                session.allowedByTicketIds.length > 0
              ) {
                session.allowedByTicketIds.forEach((ticketId, ticketIndex) => {
                  if (!ticketId || !ticketId.trim()) {
                    errors[
                      `${sessionPrefix}.allowedByTicketIds.${ticketIndex}`
                    ] = t("common.requiredField", {
                      field_name: `Session ${sessionIndex + 1} Ticket ${
                        ticketIndex + 1
                      }`,
                    });
                  }
                });
              }

              // Session tags (if present)
              if (session.tags && session.tags.length > 0) {
                session.tags.forEach((tag, tagIndex) => {
                  const tagPrefix = `${sessionPrefix}.tags.${tagIndex}`;

                  if (!tag.color || !tag.color.trim()) {
                    errors[`${tagPrefix}.color`] = t("common.requiredField", {
                      field_name: `Session ${sessionIndex + 1} Tag ${
                        tagIndex + 1
                      } Color`,
                    });
                  }
                  if (!hasMultilingualContent(tag.label)) {
                    errors[`${tagPrefix}.label`] = t("common.requiredField", {
                      field_name: `Session ${sessionIndex + 1} Tag ${
                        tagIndex + 1
                      } Label`,
                    });
                  }

                  // Validate multilingual tag label (required_with)
                  validateRequiredWithMultilingual(
                    tag.label,
                    selectedLanguages,
                    `${tagPrefix}.label`,
                    errors,
                    t,
                    `Session ${sessionIndex + 1} Tag ${tagIndex + 1} Label`
                  );
                });
              }
            });
          }
        });
      }
    });
  }

  // FAQ validation (if present)
  if (eventData.faq && eventData.faq.length > 0) {
    eventData.faq.forEach((faqItem, index) => {
      const faqPrefix = `faq.${index}`;

      if (!hasMultilingualContent(faqItem.question)) {
        errors[`${faqPrefix}.question`] = t("common.requiredField", {
          field_name: `FAQ ${index + 1} Question`,
        });
      }
      if (!hasMultilingualContent(faqItem.answer)) {
        errors[`${faqPrefix}.answer`] = t("common.requiredField", {
          field_name: `FAQ ${index + 1} Answer`,
        });
      }

      // Validate multilingual content (required_with)
      validateRequiredWithMultilingual(
        faqItem.question,
        selectedLanguages,
        `${faqPrefix}.question`,
        errors,
        t,
        `FAQ ${index + 1} Question`
      );
      validateRequiredWithMultilingual(
        faqItem.answer,
        selectedLanguages,
        `${faqPrefix}.answer`,
        errors,
        t,
        `FAQ ${index + 1} Answer`
      );
    });
  }

  // Gallery validation (if present)
  if (eventData.gallery) {
    // Validate multilingual gallery title (required_with)
    validateRequiredWithMultilingual(
      eventData.gallery.title,
      selectedLanguages,
      "gallery.title",
      errors,
      t,
      "eventDetails.photosVideos.sectionTitle"
    );

    // Validate gallery media
    validateMediaItems(eventData.gallery.media, "gallery.media", errors, t);
  }

  // WhyAttend validation (if present)
  if (eventData.whyAttend) {
    // Validate multilingual whyAttend title (required_with)
    validateRequiredWithMultilingual(
      eventData.whyAttend.title,
      selectedLanguages,
      "whyAttend.title",
      errors,
      t,
      "eventDetails.highlights.sectionTitle"
    );

    // Validate whyAttend media
    validateMediaItems(eventData.whyAttend.media, "whyAttend.media", errors, t);
  }

  // Testimonials validation (if present)
  if (eventData.testimonials) {
    // Validate multilingual testimonials title (required_with)
    validateRequiredWithMultilingual(
      eventData.testimonials.title,
      selectedLanguages,
      "testimonials.title",
      errors,
      t,
      "eventDetails.testimonials.sectionTitle"
    );

    if (
      eventData.testimonials.testimonials &&
      eventData.testimonials.testimonials.length > 0
    ) {
      eventData.testimonials.testimonials.forEach((testimonial, index) => {
        const testimonialPrefix = `testimonials.testimonials.${index}`;

        if (!hasMultilingualContent(testimonial.testimonial)) {
          errors[`${testimonialPrefix}.testimonial`] = t(
            "common.requiredField",
            {
              field_name: `Testimonial ${index + 1} Text`,
            }
          );
        }
        if (!hasMultilingualContent(testimonial.name)) {
          errors[`${testimonialPrefix}.name`] = t("common.requiredField", {
            field_name: `Testimonial ${index + 1} Name`,
          });
        }

        // Validate multilingual testimonial content (required_with)
        validateRequiredWithMultilingual(
          testimonial.testimonial,
          selectedLanguages,
          `${testimonialPrefix}.testimonial`,
          errors,
          t,
          `Testimonial ${index + 1} Text`
        );
        validateRequiredWithMultilingual(
          testimonial.name,
          selectedLanguages,
          `${testimonialPrefix}.name`,
          errors,
          t,
          `Testimonial ${index + 1} Name`
        );

        // Validate testimonial images (if present)
        if (testimonial.image && testimonial.image.length > 0) {
          testimonial.image.forEach((img, imgIndex) => {
            const imgPrefix = `${testimonialPrefix}.image.${imgIndex}`;

            if (!img.src || !img.src.trim()) {
              errors[`${imgPrefix}.src`] = t("common.requiredField", {
                field_name: `Testimonial ${index + 1} Image ${
                  imgIndex + 1
                } Source`,
              });
            }
            if (img.width === undefined || img.width === null) {
              errors[`${imgPrefix}.width`] = t("common.requiredField", {
                field_name: `Testimonial ${index + 1} Image ${
                  imgIndex + 1
                } Width`,
              });
            }
            if (img.height === undefined || img.height === null) {
              errors[`${imgPrefix}.height`] = t("common.requiredField", {
                field_name: `Testimonial ${index + 1} Image ${
                  imgIndex + 1
                } Height`,
              });
            }
            if (!img.type || !img.type.trim()) {
              errors[`${imgPrefix}.type`] = t("common.requiredField", {
                field_name: `Testimonial ${index + 1} Image ${
                  imgIndex + 1
                } Type`,
              });
            }
            if (img.size === undefined || img.size === null) {
              errors[`${imgPrefix}.size`] = t("common.requiredField", {
                field_name: `Testimonial ${index + 1} Image ${
                  imgIndex + 1
                } Size`,
              });
            }
          });
        }

        // Validate testimonial date
        if (!testimonial.date) {
          errors[`${testimonialPrefix}.date`] = t("common.requiredField", {
            field_name: `Testimonial ${index + 1} Date`,
          });
        }
      });
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Helper function to check if a multilingual field has content
 */
export function hasMultilingualContent(
  field: MultilingualText | undefined
): boolean {
  if (!field) return false;
  return Object.values(field).some((value) => value?.trim());
}

/**
 * Validates required_with fields for multilingual content
 * If any language has content, all selected languages must have content
 */
export function validateRequiredWithMultilingual(
  field: MultilingualText | undefined,
  selectedLanguages: string[],
  fieldName: string,
  errors: ValidationErrors,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
  translationKey: string
): void {
  if (!field) return;

  // Check if any language has content
  const hasAnyContent = Object.values(field).some((value) => value?.trim());

  if (hasAnyContent && selectedLanguages.length > 0) {
    // If any language has content, all selected languages must have content
    const missingLanguages = selectedLanguages.filter(
      (lang) => !field[lang]?.trim()
    );
    if (missingLanguages.length > 0) {
      const fieldDisplayName = t(translationKey) || translationKey;
      errors[
        fieldName
      ] = `${fieldDisplayName} must be filled in all supported languages`;
    }
  }
}

/**
 * Helper function to validate multilingual field for all selected languages
 */
function validateMultilingualField(
  field: MultilingualText | undefined,
  selectedLanguages: string[],
  fieldName: string,
  errors: ValidationErrors,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string,
  translationKey: string,
  isRequired: boolean = false
): void {
  if (isRequired) {
    // Field is unconditionally required
    if (selectedLanguages.length > 0) {
      const hasAllLanguages = selectedLanguages.every((lang) =>
        field?.[lang]?.trim()
      );
      if (!field || !hasAllLanguages) {
        errors[fieldName] = t("common.requiredField", {
          field_name: t(translationKey),
        });
      }
    } else {
      // Fallback: check if any language has content
      if (!hasMultilingualContent(field)) {
        errors[fieldName] = t("common.requiredField", {
          field_name: t(translationKey),
        });
      }
    }
  } else {
    // Field is conditional - use required_with validation
    validateRequiredWithMultilingual(
      field,
      selectedLanguages,
      fieldName,
      errors,
      t,
      translationKey
    );
  }
}
